import React from "react";

const MdmDeviceNotesModal = ({
  isOpen,
  onClose,
  deviceId,
  deviceIds = [],
  notes,
  isSaving,
  onSave,
  onSaveBulk,
}) => {
  const isBulkMode = Array.isArray(deviceIds) && deviceIds.length > 0;
  const [draft, setDraft] = React.useState(notes ?? "");

  React.useEffect(() => {
    if (isOpen) {
      setDraft(isBulkMode ? "" : notes ?? "");
    }
  }, [isOpen, isBulkMode, notes]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (isSaving) return;
    const next = draft ?? "";

    if (isBulkMode) {
      try {
        await onSaveBulk(deviceIds, next);
        onClose();
      } catch {
        // Parent shows error notification; keep modal open for retry.
      }
      return;
    }

    const prev = notes ?? "";
    if (next === prev) {
      onClose();
      return;
    }
    try {
      await onSave(deviceId, next, prev);
      onClose();
    } catch {
      // Parent shows error notification; keep modal open for retry.
    }
  };

  const subtitle = isBulkMode
    ? `${deviceIds.length} device${deviceIds.length === 1 ? "" : "s"} selected`
    : deviceId;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-on-surface/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-surface-container-lowest w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="p-6 border-b border-outline-variant/10 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-fixed rounded-lg text-primary">
              <span className="material-symbols-outlined">sticky_note_2</span>
            </div>
            <div>
              <h2 className="font-headline font-bold text-lg">Device Notes</h2>
              <p className="text-xs text-on-surface-variant break-all">{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            className="p-2 hover:bg-surface-container-low rounded-full transition-colors text-on-surface-variant"
            onClick={onClose}
            disabled={isSaving}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="p-6">
          <textarea
            className="w-full min-h-[10rem] text-sm text-on-surface bg-surface-container-low border border-outline-variant/30 rounded-xl px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={isSaving}
            placeholder={isBulkMode ? "Enter notes to apply to all selected devices..." : "Add notes..."}
            aria-label="Device notes"
            autoFocus
          />
        </div>
        <div className="p-6 bg-surface-container-low/50 flex items-center justify-end space-x-3">
          <button
            type="button"
            className="px-4 py-2 text-on-surface font-semibold text-sm hover:bg-surface-container-high rounded-lg transition-colors disabled:opacity-60"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-6 py-2 bg-gradient-to-br from-primary to-primary-container text-white font-bold text-sm rounded-lg shadow-lg hover:shadow-primary/20 transition-all active:scale-95 disabled:opacity-60"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MdmDeviceNotesModal;
